"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CollectorRequest = void 0;
const errors_ts_1 = require("../errors.js");
const HDSModelInitAndSingleton_ts_1 = require("../HDSModel/HDSModelInitAndSingleton.js");
const localizeText_ts_1 = require("../localizeText.js");
const Questionnaire_ts_1 = require("./Questionnaire.js");
const questionnaireCoverage_ts_1 = require("./questionnaireCoverage.js");
const VALID_CUSTOM_FIELD_EVENT_TYPES = [
    'note/txt', 'note/html', 'count/generic', 'date/iso-8601', 'activity/plain'
];
const CURRENT_VERSION = 1;
/**
 * Each Collector has one Request
 * Which contains
 * - the name of the requester
 * - a title
 * - a description
 * - a consent message
 * - a set of permission requests
 * - a version
 */
class CollectorRequest {
    #version;
    #title;
    #description;
    #consent;
    #requester;
    #app;
    #permissionsExtra;
    #permissions;
    #sections;
    #features;
    #existingStreamRefs;
    #customFields;
    #questionnaires;
    #extraContent;
    constructor(content) {
        this.#version = CURRENT_VERSION;
        this.#requester = { name: null };
        this.#app = { id: null, url: null, data: {} };
        this.#permissions = [];
        this.#permissionsExtra = [];
        this.#sections = [];
        this.#features = {};
        this.#existingStreamRefs = [];
        this.#customFields = [];
        this.#questionnaires = [];
        this.setContent(content);
    }
    /**
     * Temp content
     * @param content
     */
    setContent(content) {
        const futureContent = structuredClone(content);
        // validate content
        if (futureContent.version != null) {
            const numV = Number.parseInt(futureContent.version);
            if (numV === 0) {
                vo0ToV1(futureContent); // convert to v1 if needed
            }
            else {
                if (numV !== this.#version)
                    throw new errors_ts_1.HDSLibError(`Invalid CollectorRequest content version: ${futureContent.version}`);
            }
            delete futureContent.version;
        }
        // -- title, consent, description
        for (const key of ['title', 'consent', 'description']) {
            if (futureContent[key] != null) {
                this[key] = futureContent[key];
            }
            delete futureContent[key];
        }
        // -- requester
        if (futureContent.requester) {
            if (futureContent.requester.name != null) {
                this.requesterName = futureContent.requester.name;
            }
            delete futureContent.requester;
        }
        // -- app
        if (futureContent.app) {
            if (futureContent.app.id != null) {
                this.appId = futureContent.app.id;
            }
            if (futureContent.app.url != null) {
                this.appUrl = futureContent.app.url;
            }
            if (futureContent.app.data != null) {
                this.appCustomData = futureContent.app.data;
            }
            delete futureContent.app;
        }
        // -- sections
        if (futureContent.sections != null) {
            this.#sections = []; // reset sections
            for (const sectionData of futureContent.sections) {
                const section = this.createSection(sectionData.key, sectionData.type);
                section.setName(sectionData.name);
                section.addItemKeys(sectionData.itemKeys);
                if (sectionData.itemCustomizations) {
                    for (const [itemKey, customization] of Object.entries(sectionData.itemCustomizations)) {
                        section.setItemCustomization(itemKey, customization);
                    }
                }
                if (Array.isArray(sectionData.customFieldKeys)) {
                    section.setCustomFieldKeys(sectionData.customFieldKeys);
                }
            }
            delete futureContent.sections;
        }
        // -- permissions
        if (futureContent.permissions) {
            this.#permissions = []; // reset permissions
            futureContent.permissions.forEach((p) => {
                this.addPermission(p.streamId, p.defaultName, p.level);
            });
            delete futureContent.permissions;
        }
        // -- permissionsExtra
        if (futureContent.permissionsExtra) {
            this.#permissionsExtra = []; // reset permissions Extra
            futureContent.permissionsExtra.forEach((p) => {
                this.addPermissionExtra(p);
            });
            delete futureContent.permissionsExtra;
        }
        // -- features
        if (futureContent.features) {
            if (futureContent.features.chat != null) {
                this.addChatFeature(futureContent.features.chat);
            }
            delete futureContent.features.chat;
            if (Object.keys(futureContent.features).length > 0) {
                throw new errors_ts_1.HDSLibError('Found unkown features', futureContent.features);
            }
            delete futureContent.features;
        }
        // -- existingStreamRefs (Plan 45 mode-3)
        if (futureContent.existingStreamRefs) {
            this.#existingStreamRefs = [];
            for (const ref of futureContent.existingStreamRefs) {
                this.addExistingStreamRef(ref);
            }
            delete futureContent.existingStreamRefs;
        }
        // -- customFields (Plan 45 mode-2 — provision-new template-private streams)
        if (futureContent.customFields) {
            this.#customFields = [];
            for (const cf of futureContent.customFields) {
                this.addCustomField(cf);
            }
            delete futureContent.customFields;
        }
        // -- questionnaires (Plan 71 — bundle Questionnaire(s) into first-contact
        //    request; on patient accept the patient app writes one
        //    questionnaire/request-v1 event per entry in the patient's stream,
        //    see Questionnaire.writeBundled).
        //    For an established relationship the doctor sends a bare Questionnaire
        //    directly — no CollectorRequest involved.
        if (futureContent.questionnaires) {
            if (!Array.isArray(futureContent.questionnaires)) {
                throw new errors_ts_1.HDSLibError("CollectorRequest 'questionnaires' must be an array");
            }
            this.#questionnaires = [];
            for (const q of futureContent.questionnaires) {
                this.addQuestionnaire(q);
            }
            delete futureContent.questionnaires;
        }
        this.#extraContent = futureContent;
    }
    // ------------- getter and setters ------------ //
    get version() { return this.#version; }
    set title(title) { this.#title = (0, localizeText_ts_1.validateLocalizableText)('title', asLocalizableText(title)); }
    get title() { return this.#title; }
    set consent(consent) { this.#consent = (0, localizeText_ts_1.validateLocalizableText)('consent', asLocalizableText(consent)); }
    get consent() { return this.#consent; }
    set description(description) { this.#description = (0, localizeText_ts_1.validateLocalizableText)('description', asLocalizableText(description)); }
    get description() { return this.#description; }
    set requesterName(name) { this.#requester.name = validateString('requester:name', name); }
    get requesterName() { return this.#requester.name; }
    set appId(id) { this.#app.id = validateString('app:id', id); }
    get appId() { return this.#app.id; }
    set appUrl(url) { this.#app.url = validateString('app:url', url); }
    get appUrl() { return this.#app.url; }
    set appCustomData(data) { this.#app.data = data; }
    get appCustomData() { return this.#app.data; }
    get permissions() { return this.#permissions; }
    get permissionsExtra() { return this.#permissionsExtra; }
    get features() { return this.#features; }
    // --- section --- //
    get sections() {
        return this.#sections;
    }
    get sectionsData() {
        const result = [];
        for (const section of this.#sections) {
            const data = section.getData();
            result.push(data);
        }
        return result;
    }
    createSection(key, type) {
        if (this.getSectionByKey(key) != null)
            throw new errors_ts_1.HDSLibError(`Section with key: ${key} already exists`);
        const section = new CollectorRequestSection(key, type);
        this.#sections.push(section);
        return section;
    }
    getSectionByKey(key) {
        return this.#sections.find((s) => (s.key === key));
    }
    moveSection(key, toIndex) {
        const idx = this.#sections.findIndex((s) => s.key === key);
        if (idx === -1)
            throw new errors_ts_1.HDSLibError(`Section with key: ${key} not found`);
        const [section] = this.#sections.splice(idx, 1);
        this.#sections.splice(toIndex, 0, section);
    }
    removeSection(key) {
        const idx = this.#sections.findIndex((s) => s.key === key);
        if (idx === -1)
            throw new errors_ts_1.HDSLibError(`Section with key: ${key} not found`);
        this.#sections.splice(idx, 1);
    }
    // ---------- permissions ---------- //
    addPermissions(permissions) {
        for (const permission of permissions) {
            this.addPermission(permission.streamId, permission.defaultName, permission.level);
        }
    }
    addPermission(streamId, defaultName, level) {
        this.#permissions.push({ streamId, defaultName, level });
    }
    /**
     * Add a static permission, not linked to itemKeys for other usages
     * @param permission
     */
    addPermissionExtra(permission) {
        // todo standard checks
        this.#permissionsExtra.push(permission);
    }
    /**
     * Reset permissions
     */
    resetPermissions() {
        this.#permissions.splice(0, this.#permissions.length);
    }
    /**
     * Rebuild permissions based on sections itemKeys and staticPermissions
     */
    buildPermissions() {
        // 1- get all items form the questionnary sections
        const itemKeys = [];
        for (const section of this.sections) {
            itemKeys.push(...section.itemKeys);
        }
        // 2 - get the permissions with eventual preRequest
        const preRequest = this.permissionsExtra || [];
        const permissions = (0, HDSModelInitAndSingleton_ts_1.getModel)().authorizations.forItemKeys(itemKeys, { preRequest });
        // 3 - if no error araised - reset permissions
        this.resetPermissions();
        this.addPermissions(permissions);
    }
    // ---------- features ------------- //
    get hasChatFeature() {
        return this.#features.chat != null;
    }
    /**
     * Also accepts the FormSpec / AppTemplate boolean shape: `true` is `{ type: 'user' }`, `false` is no chat.
     */
    addChatFeature(settings = { type: 'user' }) {
        if (settings === false) {
            delete this.#features.chat;
            return;
        }
        if (settings === true)
            settings = { type: 'user' };
        if (settings == null || !['user', 'usernames'].includes(settings.type))
            throw new errors_ts_1.HDSLibError('Invalid chat type', settings);
        this.#features.chat = settings;
    }
    // ---------- existingStreamRefs (Plan 45 mode-3) ----------- //
    get existingStreamRefs() { return this.#existingStreamRefs; }
    /**
     * Request access on a pre-existing stream (Plan 45 mode-3). Typing of inner
     * events is owned by the stream's existing `clientData`; this CollectorRequest
     * only patches access permissions at acceptance.
     */
    addExistingStreamRef(ref) {
        if (ref == null || typeof ref !== 'object')
            throw new errors_ts_1.HDSLibError('Invalid existingStreamRef', ref);
        if (typeof ref.streamId !== 'string' || ref.streamId.length === 0) {
            throw new errors_ts_1.HDSLibError('existingStreamRef.streamId must be a non-empty string', ref);
        }
        if (!Array.isArray(ref.permissions) || ref.permissions.length === 0) {
            throw new errors_ts_1.HDSLibError('existingStreamRef.permissions must be a non-empty array', ref);
        }
        for (const p of ref.permissions) {
            if (!['read', 'manage', 'contribute'].includes(p)) {
                throw new errors_ts_1.HDSLibError(`Invalid permission level "${p}" in existingStreamRef`, ref);
            }
        }
        this.#existingStreamRefs.push({
            streamId: ref.streamId,
            permissions: ref.permissions,
            ...(ref.purpose != null ? { purpose: ref.purpose } : {}),
            ...(ref.label != null ? { label: ref.label } : {})
        });
    }
    // ---------- customFields (Plan 45 mode-2) ----------- //
    get customFields() { return this.#customFields; }
    /**
     * Declare a template-private stream to be provisioned at acceptance with its
     * `clientData.hdsCustomField[<eventType>]` block. Sandbox prefix is enforced
     * structurally (streamId must start with `${templateId}-`); cross-template
     * collisions are rejected by the loader.
     *
     * The CollectorRequest itself does not know its enclosing template id (the
     * template's), so we validate against `def.templateId` here as a
     * defence-in-depth check — `loader.ts` is the canonical enforcer.
     */
    addCustomField(cf) {
        if (cf == null || typeof cf !== 'object')
            throw new errors_ts_1.HDSLibError('Invalid customField', cf);
        if (typeof cf.streamId !== 'string' || cf.streamId.length === 0) {
            throw new errors_ts_1.HDSLibError('customField.streamId must be a non-empty string', cf);
        }
        if (!VALID_CUSTOM_FIELD_EVENT_TYPES.includes(cf.eventType)) {
            throw new errors_ts_1.HDSLibError(`Invalid customField.eventType "${cf.eventType}" — must be one of ${VALID_CUSTOM_FIELD_EVENT_TYPES.join(', ')}`, cf);
        }
        if (cf.def == null || typeof cf.def !== 'object') {
            throw new errors_ts_1.HDSLibError('customField.def must be an object', cf);
        }
        if (cf.def.version !== 'v1') {
            throw new errors_ts_1.HDSLibError(`Unsupported customField.def.version "${cf.def.version}"`, cf);
        }
        if (typeof cf.def.templateId !== 'string' || cf.def.templateId.length === 0) {
            throw new errors_ts_1.HDSLibError('customField.def.templateId must be a non-empty string', cf);
        }
        if (typeof cf.def.key !== 'string' || cf.def.key.length === 0) {
            throw new errors_ts_1.HDSLibError('customField.def.key must be a non-empty string', cf);
        }
        if (!cf.streamId.startsWith(cf.def.templateId + '-')) {
            throw new errors_ts_1.HDSLibError(`customField.streamId "${cf.streamId}" violates sandbox prefix — must start with "${cf.def.templateId}-"`, cf);
        }
        this.#customFields.push({
            streamId: cf.streamId,
            eventType: cf.eventType,
            def: cf.def,
            ...(cf.parentId != null ? { parentId: cf.parentId } : {}),
            ...(cf.name != null ? { name: cf.name } : {})
        });
    }
    // ---------- questionnaires (Plan 71) ---------- //
    get questionnaires() {
        return this.#questionnaires;
    }
    /**
     * Add a Questionnaire to this request. Accepts either a Questionnaire
     * instance (its `toRequestEventContent()` payload is stored) or a raw
     * `QuestionnaireRequestContent` object (validated through a Questionnaire
     * round-trip so we surface errors at add-time rather than at send-time).
     *
     * On patient accept, the patient app writes one `questionnaire/request-v1`
     * event per stored entry (`Questionnaire.writeBundled`). For pre-accept inspection
     * (e.g. preview UI) consumers can iterate `request.questionnaires`.
     */
    addQuestionnaire(q) {
        const content = (q instanceof Questionnaire_ts_1.Questionnaire)
            ? q.toRequestEventContent()
            : new Questionnaire_ts_1.Questionnaire(q).toRequestEventContent();
        this.#questionnaires.push(content);
        return content;
    }
    /** Convenience: return a fresh Questionnaire instance over the stored entry at `index`. */
    getQuestionnaire(index) {
        const content = this.#questionnaires[index];
        if (content == null)
            return null;
        return new Questionnaire_ts_1.Questionnaire(content);
    }
    /** Remove the entry at `index`. Returns true if it existed. */
    removeQuestionnaire(index) {
        if (index < 0 || index >= this.#questionnaires.length)
            return false;
        this.#questionnaires.splice(index, 1);
        return true;
    }
    /**
     * Check whether this request's permissions cover every item referenced by
     * the given Questionnaire. See `checkQuestionnaireCoverage` for the report
     * shape. Read-only — does not mutate this request.
     */
    checkQuestionnaireCoverage(q) {
        return (0, questionnaireCoverage_ts_1.checkQuestionnaireCoverage)(q, this);
    }
    /**
     * Compute the coverage report for the given Questionnaire and, if any
     * permissions are missing, add them to this request in place. Returns the
     * report so callers can surface unknownItems / proposedPermissions to the
     * doctor (e.g. "Added 3 permissions to your request: body-weight, ...").
     * They are also kept in `permissionsExtra`, so `buildPermissions()` (which
     * rebuilds from the sections' item keys) does not drop them on save.
     */
    applyQuestionnaireCoverage(q) {
        const report = (0, questionnaireCoverage_ts_1.checkQuestionnaireCoverage)(q, this);
        for (const p of report.proposedPermissions) {
            this.addPermission(p.streamId, p.defaultName, p.level);
            if (!this.#permissionsExtra.some((e) => e.streamId === p.streamId)) {
                this.addPermissionExtra({ streamId: p.streamId, defaultName: p.defaultName, level: p.level });
            }
        }
        return report;
    }
    // ---------- sections ------------- //
    /**
     * Return Content to comply with initial implementation as an object
     */
    get content() {
        const content = {
            version: this.version,
            title: this.title,
            consent: this.consent,
            description: this.description,
            requester: {
                name: this.requesterName
            },
            features: this.features,
            permissionsExtra: this.permissionsExtra,
            permissions: this.permissions,
            app: {
                id: this.appId,
                url: this.appUrl,
                data: this.appCustomData
            },
            sections: this.sectionsData
        };
        if (this.#existingStreamRefs.length > 0) {
            content.existingStreamRefs = this.#existingStreamRefs;
        }
        if (this.#customFields.length > 0) {
            content.customFields = this.#customFields;
        }
        if (this.#questionnaires.length > 0) {
            content.questionnaires = this.#questionnaires;
        }
        Object.assign(content, this.#extraContent);
        return content;
    }
}
exports.CollectorRequest = CollectorRequest;
/** A plain string localizable text (allowed by the AppTemplate schema) is the English text. */
function asLocalizableText(text) {
    return (typeof text === 'string') ? { en: text } : text;
}
function validateString(key, totest) {
    if (totest == null || typeof totest !== 'string')
        throw new errors_ts_1.HDSLibError(`Invalid ${key} value: ${totest}`, { [key]: totest });
    return totest;
}
class CollectorRequestSection {
    #type;
    #name;
    #key;
    #itemKeys;
    #itemCustomizations;
    #customFieldKeys;
    constructor(key, type) {
        this.#key = key;
        this.#type = type;
        this.#itemKeys = [];
        this.#itemCustomizations = {};
        this.#customFieldKeys = [];
        this.#name = {
            en: ''
        };
    }
    addItemKeys(keys) {
        keys.forEach((k) => this.addItemKey(k));
    }
    addItemKey(key) {
        (0, HDSModelInitAndSingleton_ts_1.getModel)().itemsDefs.forKey(key); // will throw error if not found
        if (this.#itemKeys.includes(key))
            return; // avoid double entries
        this.#itemKeys.push(key);
    }
    setName(localizedName) {
        // the AppTemplate schema allows a plain string (English); Object.entries on it would store character indexes
        for (const [languageCode, name] of Object.entries(asLocalizableText(localizedName))) {
            this.setNameLocal(languageCode, name);
        }
    }
    setNameLocal(languageCode, name) {
        this.#name[languageCode] = name;
    }
    get type() { return this.#type; }
    get key() { return this.#key; }
    get itemKeys() { return this.#itemKeys; }
    get name() { return this.#name; }
    get itemCustomizations() { return this.#itemCustomizations; }
    moveItemKey(key, toIndex) {
        const idx = this.#itemKeys.indexOf(key);
        if (idx === -1)
            throw new errors_ts_1.HDSLibError(`ItemKey: ${key} not found in section: ${this.#key}`);
        this.#itemKeys.splice(idx, 1);
        this.#itemKeys.splice(toIndex, 0, key);
    }
    removeItemKey(key) {
        const idx = this.#itemKeys.indexOf(key);
        if (idx === -1)
            throw new errors_ts_1.HDSLibError(`ItemKey: ${key} not found in section: ${this.#key}`);
        this.#itemKeys.splice(idx, 1);
    }
    setItemCustomization(key, customizations) {
        this.#itemCustomizations[key] = customizations;
    }
    getItemCustomization(key) {
        return this.#itemCustomizations[key];
    }
    // Plan 45 — section's reference into request.customFields[]
    get customFieldKeys() { return this.#customFieldKeys; }
    setCustomFieldKeys(keys) {
        if (!Array.isArray(keys))
            throw new errors_ts_1.HDSLibError('customFieldKeys must be an array', keys);
        this.#customFieldKeys = keys.slice();
    }
    addCustomFieldKey(key) {
        if (typeof key !== 'string' || key.length === 0)
            throw new errors_ts_1.HDSLibError('customFieldKey must be a non-empty string', key);
        if (this.#customFieldKeys.includes(key))
            return;
        this.#customFieldKeys.push(key);
    }
    getData() {
        const data = {
            key: this.key,
            type: this.#type,
            name: this.#name,
            itemKeys: this.#itemKeys
        };
        if (Object.keys(this.#itemCustomizations).length > 0) {
            data.itemCustomizations = this.#itemCustomizations;
        }
        if (this.#customFieldKeys.length > 0) {
            data.customFieldKeys = this.#customFieldKeys;
        }
        return data;
    }
}
/**
 * Transform data to match v1
 * @param v0Data
 */
function vo0ToV1(v0Data) {
    if (v0Data.app?.data?.forms) {
        if (v0Data.sections)
            throw new errors_ts_1.HDSLibError('Cannot mix data.forms & sections', v0Data);
        v0Data.sections = [];
        for (const [key, value] of Object.entries(v0Data.app.data.forms)) {
            value.key = key;
            value.name = {
                en: value.name
            };
            v0Data.sections.push(value);
        }
        delete v0Data.app.data.forms;
    }
    v0Data.version = 1;
}
